import React, { useEffect, useRef, useState } from 'react';

/**
 * AimTest — Module 2 (replaces Composure/Flanker)
 * Mouse-based aim precision test.
 * 25 targets appear at random positions; player clicks each as quickly and accurately as possible.
 * Records RT, hit/miss, and normalized distance from center.
 */
export default function AimTest({ trials, onComplete, icon, moduleNum = 2, totalModules = 6 }) {
  const canvasRef = useRef(null);
  const stateRef = useRef({
    trialIndex: 0,
    onsetTime: null,
    awaitingResponse: false,
    results: [],
    timeoutId: null,
  });

  // Refs for functions to avoid circular dependency / stale closure issues
  const runNextTrialRef = useRef(null);
  const recordResponseRef = useRef(null);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  const warmupPhase = useRef(true);
  const [warmupComplete, setWarmupComplete] = useState(false);

  const [uiState, setUiState] = useState('instructions'); // instructions | running | done
  const [progress, setProgress] = useState(0);
  const [canvasSize, setCanvasSize] = useState({ w: 800, h: 520 });

  const WARMUP_AIMS = [
    { x: 0.5, y: 0.5, radius: 60, isi: 700 },
    { x: 0.28, y: 0.38, radius: 55, isi: 700 },
    { x: 0.72, y: 0.62, radius: 55, isi: 700 },
  ];

  useEffect(() => {
    document.body.style.cursor = uiState === 'running' ? 'crosshair' : '';
    return () => { document.body.style.cursor = ''; };
  }, [uiState]);

  // --- Canvas drawing ---

  const drawTrial = (ctx, canvas, trial, flash = null) => {
    ctx.fillStyle = '#0a0e1a';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const cx = trial.x * canvas.width;
    const cy = trial.y * canvas.height;
    const radius = trial.radius || 40;

    if (flash) {
      ctx.fillStyle = flash === 'hit'
        ? 'rgba(16,185,129,0.2)'
        : 'rgba(239,68,68,0.2)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }

    ctx.beginPath();
    ctx.arc(cx, cy, radius, 0, Math.PI * 2);
    ctx.fillStyle = flash === 'hit' ? '#10b981' : flash === 'miss' ? '#ef4444' : '#00d4ff';
    ctx.fill();
    ctx.strokeStyle = 'rgba(255,255,255,0.25)';
    ctx.lineWidth = 2;
    ctx.stroke();

    // Center dot
    ctx.beginPath();
    ctx.arc(cx, cy, 4, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(255,255,255,0.6)';
    ctx.fill();
  };

  const drawWaiting = (ctx, canvas) => {
    ctx.fillStyle = '#0a0e1a';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  };

  // --- Core logic via refs ---

  recordResponseRef.current = (trial, clickX, clickY) => {
    // clickX/clickY are null for auto-miss (timeout)
    const s = stateRef.current;
    s.awaitingResponse = false;
    clearTimeout(s.timeoutId);

    const canvas = canvasRef.current;
    const ctx = canvas ? canvas.getContext('2d') : null;

    let rt = null;
    let correct = false;
    let distFromCenter = 2.0; // penalty for auto-miss

    if (clickX !== null && clickY !== null && canvas) {
      rt = Math.round(performance.now() - s.onsetTime);
      const tcx = trial.x * canvas.width;
      const tcy = trial.y * canvas.height;
      const dx = clickX - tcx;
      const dy = clickY - tcy;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const radius = trial.radius || 40;
      correct = dist <= radius;
      distFromCenter = dist / radius;
    }

    if (ctx && canvas) {
      requestAnimationFrame(() => drawTrial(ctx, canvas, trial, correct ? 'hit' : 'miss'));
    }

    // Only push to results during real test
    if (!warmupPhase.current) {
      s.results.push({ type: 'aim', rt, correct, distFromCenter, radius: trial.radius || 40 });
    }
    s.trialIndex++;

    setTimeout(() => runNextTrialRef.current?.(), 300);
  };

  runNextTrialRef.current = () => {
    const s = stateRef.current;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    const activeTrials = warmupPhase.current ? WARMUP_AIMS : trials;

    if (s.trialIndex >= activeTrials.length) {
      if (warmupPhase.current) {
        // Warmup finished — show overlay then start real test
        setWarmupComplete(true);
        setTimeout(() => {
          warmupPhase.current = false;
          s.trialIndex = 0;
          s.results = [];
          s.awaitingResponse = false;
          setWarmupComplete(false);
          setProgress(0);
          setTimeout(() => runNextTrialRef.current?.(), 200);
        }, 2000);
        return;
      }
      setUiState('done');
      onCompleteRef.current(s.results);
      return;
    }

    const trial = activeTrials[s.trialIndex];
    s.awaitingResponse = false;
    s.onsetTime = null;

    drawWaiting(ctx, canvas);
    if (!warmupPhase.current) {
      setProgress(Math.round((s.trialIndex / trials.length) * 100));
    }

    s.timeoutId = setTimeout(() => {
      requestAnimationFrame(() => {
        drawTrial(ctx, canvas, trial);
        s.onsetTime = performance.now();
        s.awaitingResponse = true;

        // Auto-miss after 2000ms
        s.timeoutId = setTimeout(() => {
          if (s.awaitingResponse) {
            recordResponseRef.current(trial, null, null);
          }
        }, 2000);
      });
    }, trial.isi || 800);
  };

  // --- Input handlers (mouse + touch) ---

  useEffect(() => {
    if (uiState !== 'running') return;
    const canvas = canvasRef.current;
    if (!canvas) return;

    const getCanvasCoords = (clientX, clientY) => {
      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;
      return {
        x: (clientX - rect.left) * scaleX,
        y: (clientY - rect.top) * scaleY,
      };
    };

    const handleInput = (clientX, clientY) => {
      const s = stateRef.current;
      if (!s.awaitingResponse || s.onsetTime === null) return;
      const { x, y } = getCanvasCoords(clientX, clientY);
      const activeTrials = warmupPhase.current ? WARMUP_AIMS : trials;
      const trial = activeTrials[s.trialIndex];
      recordResponseRef.current(trial, x, y);
    };

    const handleMouseDown = (e) => handleInput(e.clientX, e.clientY);

    const handleTouchStart = (e) => {
      e.preventDefault(); // block page scroll/zoom during test
      const touch = e.changedTouches[0];
      handleInput(touch.clientX, touch.clientY);
    };

    canvas.addEventListener('mousedown', handleMouseDown);
    canvas.addEventListener('touchstart', handleTouchStart, { passive: false });
    return () => {
      canvas.removeEventListener('mousedown', handleMouseDown);
      canvas.removeEventListener('touchstart', handleTouchStart);
    };
  }, [uiState]); // eslint-disable-line

  // --- Cleanup ---

  useEffect(() => {
    return () => {
      const s = stateRef.current;
      clearTimeout(s.timeoutId);
    };
  }, []);

  // --- Start ---

  const startTest = () => {
    stateRef.current.trialIndex = 0;
    stateRef.current.results = [];
    stateRef.current.awaitingResponse = false;
    setProgress(0);
    setUiState('running');
    setTimeout(() => runNextTrialRef.current?.(), 500);
  };

  const startWarmup = () => {
    setCanvasSize({ w: window.innerWidth, h: window.innerHeight });
    warmupPhase.current = true;
    startTest();
  };

  // --- Render ---

  return (
    <div style={{ width: '100%' }}>
      {uiState === 'instructions' && (
        <div className="card text-center" style={{ maxWidth: 480, margin: '0 auto' }}>
          {icon && <div style={{ width: 56, height: 56, borderRadius: 14, background: 'var(--accent-dim)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--accent)', margin: '0 auto 20px' }}>{icon}</div>}
          <h2 style={{ marginBottom: 8 }}>Aim Precision</h2>
          <p style={{ marginBottom: 8 }}>
            Circles will appear at random positions on screen. Click each circle as quickly and accurately as possible.
          </p>
          <p className="text-muted" style={{ fontSize: 13, marginBottom: 4 }}>
            Input: <span className="text-accent mono">CLICK / TAP</span>
          </p>
          <p className="text-muted" style={{ fontSize: 13, marginBottom: 24 }}>
            25 targets — click the center of each ring for maximum score.
          </p>
          <button className="btn btn--primary" onClick={startWarmup}>
            Start
          </button>
        </div>
      )}

      {uiState === 'running' && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          background: '#0a0e1a', overflow: 'hidden',
        }}>
          {/* Progress bar overlay */}
          <div style={{
            position: 'absolute', top: 16, left: 16, right: 16, zIndex: 1,
            display: 'flex', alignItems: 'center', gap: 12,
          }}>
            <div className="progress-bar" style={{ flex: 1 }}>
              <div className="progress-bar__fill" style={{ width: `${progress}%` }} />
            </div>
            <span style={{ fontSize: 13, whiteSpace: 'nowrap', color: warmupPhase.current && !warmupComplete ? '#f59e0b' : 'var(--text-muted)' }}>
              {warmupPhase.current && !warmupComplete ? 'PRACTICE — not scored' : `${progress}%`}
            </span>
          </div>

          <canvas
            ref={canvasRef}
            width={canvasSize.w}
            height={canvasSize.h}
            style={{ width: '100%', height: '100%', display: 'block', cursor: 'crosshair' }}
          />

          {/* Module label */}
          <div style={{
            position: 'absolute', bottom: 16, left: 0, right: 0,
            textAlign: 'center', fontSize: 13, color: 'var(--text-muted)',
            pointerEvents: 'none',
          }}>
            Module {moduleNum} of {totalModules} — Aim Precision
          </div>

          {warmupComplete && (
            <div style={{
              position: 'absolute', inset: 0,
              background: 'rgba(10,14,26,0.92)',
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>✓</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: '#10b981', marginBottom: 8 }}>
                Warmup Complete!
              </div>
              <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.6)' }}>
                Starting real test in 2 seconds...
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
